import { useEffect, useMemo, useState, useLayoutEffect } from 'react'

import { useNavigate } from 'react-router-dom'

import { useChainModal } from '@rainbow-me/rainbowkit'

import { useRequest } from 'hooks/api'
import { useAirdropState } from 'hooks/airdrop'
import { useIsWrongChainId } from 'hooks/wallet'

import {
  useAirdropParams,
  usePrepareTxsList,
  useRecipientsList,
  useExceptionsList,
  useTransactionsCount,
  useIsAirdropNotStarted,
} from 'state/airdrop/hooks'

import { Button, Notification } from 'components/ui'

import { Links } from 'constants/link'
import { errors } from 'constants/errors'
import { CHAIN_INFO } from 'constants/chains'
import { manualBatchCount } from 'constants/variables'
import { parseErrorToUserReadableMessage } from 'utils/errors'

import { Stats } from './Stats'
import { BalanceError } from './BalanceError'
import { StrategySelector } from './StrategySelector'

import { Statuses } from './@types'
import { AirdropContent } from './AirdropContent'
import { SectionLoader } from '../ApprovalForm/Recipients/Loader'

import { Section } from './Style'

export function AirdropForm() {
  const isWrongChainId = useIsWrongChainId()

  const recipientsList = useRecipientsList()
  const exceptionsList = useExceptionsList()

  const airdropState = useAirdropState()
  const airdropParams = useAirdropParams()
  const transactionsCount = useTransactionsCount()
  const isAirdropNotStarted = useIsAirdropNotStarted()

  const prepareTxsList = useRequest(usePrepareTxsList())

  const navigate = useNavigate()
  const { openChainModal } = useChainModal()

  const [showStrategyBlock, setShowStrategyBlock] = useState(false)

  const isManualBatchCount = useMemo(() => transactionsCount >= manualBatchCount, [transactionsCount, manualBatchCount])

  useLayoutEffect(() => {
    if (isManualBatchCount) {
      onBackToStrategy()
    } else {
      prepareTxsList.call()
    }
  }, [transactionsCount])

  useEffect(() => {
    if (!isAirdropNotStarted) {
      onStrategyBlockToggle(false)
    }
  }, [isAirdropNotStarted])

  const onBackToStrategy = () => {
    onStrategyBlockToggle(true)
  }

  const onStrategyBlockToggle = (shouldShowStrategy: boolean) => {
    setShowStrategyBlock(shouldShowStrategy)
  }

  const isInsufficientBalances = useMemo(
    () => !isWrongChainId && (!airdropState.isBalanceEnough || !airdropState.isTokensBalanceEnough),
    [isWrongChainId, airdropState.isBalanceEnough, airdropState.isTokensBalanceEnough],
  )

  const prepareTxListError = useMemo(() => {
    if (!prepareTxsList.errorMessage) {
      return ''
    }
    return parseErrorToUserReadableMessage(prepareTxsList.errorMessage)
  }, [prepareTxsList.errorMessage])

  const isEmulationFailed = useMemo(() => {
    return exceptionsList.length === recipientsList.length
  }, [exceptionsList.length, recipientsList.length])

  const isCriticalError = useMemo(() => {
    return !isEmulationFailed && prepareTxsList.isError && !isInsufficientBalances
  }, [isEmulationFailed, prepareTxsList.isError, isInsufficientBalances])

  const isDisabled = useMemo(() => {
    return isWrongChainId || isInsufficientBalances || isEmulationFailed || prepareTxsList.isError
  }, [isWrongChainId, isInsufficientBalances, isEmulationFailed, prepareTxsList.isError])

  const shouldShowRestartButton = useMemo(() => {
    return isInsufficientBalances || prepareTxsList.isError || isEmulationFailed
  }, [isInsufficientBalances, prepareTxsList.isError, isEmulationFailed])

  const visibleStats = useMemo(() => {
    const _visibleStats = [Statuses.TOTAL_TOKENS_TO_SEND, Statuses.TOKEN_BALANCE]
    if (!showStrategyBlock && !prepareTxsList.isError) {
      return _visibleStats.concat(
        Statuses.APPROXIMATE_COST,
        Statuses.TOTAL_ADDRESSES,
        Statuses.TOTAL_TXS,
        Statuses.NATIVE_CURR,
      )
    }
    return _visibleStats
  }, [showStrategyBlock, prepareTxsList.isError])

  const onOpenChainChangeModal = () => {
    if (openChainModal) {
      openChainModal()
    }
  }

  const backToPrepare = () => {
    navigate(Links.TOKEN)
  }

  return (
    <>
      <Stats status={visibleStats} layout={showStrategyBlock ? 'strategy' : 'distribution'} />

      <Section>
        {isWrongChainId && (
          <Notification
            isClickable
            type='error'
            margin='0 auto 1.6rem'
            onClick={onOpenChainChangeModal}
            message={`Currently, airdrop is available only on ${
              CHAIN_INFO[airdropParams.airdropChainId].name
            }. Change network to continue`}
          />
        )}

        {isInsufficientBalances && <BalanceError />}

        {prepareTxsList.isLoading && <SectionLoader />}

        {isCriticalError && <Notification type='error' margin='0 auto 1.6rem' message={prepareTxListError} />}

        {showStrategyBlock ? (
          <StrategySelector isDisabled={isDisabled} onStrategyBlockToggle={onStrategyBlockToggle} />
        ) : (
          <AirdropContent
            isDisabled={isDisabled}
            backToStrategy={onBackToStrategy}
            withBackButton={isManualBatchCount}
          />
        )}

        {shouldShowRestartButton && (
          <>
            <br />
            {isEmulationFailed && (
              <>
                <Notification type='error' message={errors.validation.EMULATION_FAILED} />
                <br />
              </>
            )}
            <Button fullSize type='outline' onClick={backToPrepare}>
              _start-new-sending
            </Button>
          </>
        )}
      </Section>
    </>
  )
}
